import React, { useMemo } from 'react';
import Input from '@components/Input';
import LicensePlateInput from '@components/LicensePlateInput/LicensePlateInput';
import ConsentCheckbox from '@features/Pip/components/ConsentCheckbox/ConsentCheckbox';
import Button from '@components/Button';
import { useTranslation } from 'next-i18next';
import styles from '../CampaignAccordion.module.css';
import CampaignSummary from '../../CampaignSummary/CampaignSummary';
import { LandingpageProductResponse } from '@shared/types/campaigns';
import { Languages } from '@shared/types';
import { useRouter } from 'next/router';
import { FormikProps } from 'formik';
import { CampaignFormValues } from '../../DefaultCampaign/DefaultCampaign';
import MultiLicensePlateInput from '@components/LicensePlateInput/MultiLicensePlateInput';
import { findWordPressTranslation } from '@utilities/WordPress/findWordPressTranslation';
import { useSelector } from 'react-redux';
import { RootState } from '@shared/store';
import HTMLText from '@features/PageBuilder/components/HTMLText';

const {
  SignUpContainer,
  SubmitButton,
  LoginButton,
  LoginButtonText,
  ConsentText,
} = styles;

interface CampaignSignUpProps {
  formik: FormikProps<CampaignFormValues>;
  isLoading: boolean;
  phoneNumberPrefix: string;
  toggleForm: () => void;
  priceInformation?: LandingpageProductResponse;
  submitButtonLabel: string;
  isCarRegistration?: boolean;
  additionalTerms?: string;
  allowMultipleCars?: boolean;
}

const CampaignSignUp: React.FC<CampaignSignUpProps> = ({
  formik,
  isLoading,
  phoneNumberPrefix,
  toggleForm,
  priceInformation,
  submitButtonLabel,
  isCarRegistration,
  additionalTerms,
  allowMultipleCars = false,
}) => {
  const { t } = useTranslation(['common', 'account', 'campaign']);

  const {
    options: { translations },
  } = useSelector((state: RootState) => state.wordpress);

  const consentWPTranslation = findWordPressTranslation(
    translations,
    'newUserConsent'
  );

  const { locale } = useRouter();
  const routerLocale = locale?.toUpperCase() as keyof typeof Languages;

  // Utility function to format phone numbers based on locale
  const formatPhoneForDisplay = (
    phone: string,
    locale: keyof typeof Languages,
    prefix: string
  ): string => {
    if (!phone) return '';

    const cleanPhone = phone.replace(/[^0-9+]/g, '');

    if (!cleanPhone.startsWith(prefix)) {
      return prefix;
    }

    const number = cleanPhone.slice(prefix.length);

    switch (locale) {
      case Languages.DA:
        // Denmark: +45 XX XX XX XX (8 digits)
        if (number.length <= 2) return `${prefix}${number}`;
        if (number.length <= 4)
          return `${prefix} ${number.slice(0, 2)} ${number.slice(2)}`;
        if (number.length <= 6)
          return `${prefix} ${number.slice(0, 2)} ${number.slice(
            2,
            4
          )} ${number.slice(4)}`;
        return `${prefix} ${number.slice(0, 2)} ${number.slice(
          2,
          4
        )} ${number.slice(4, 6)} ${number.slice(6, 8)}`;

      case Languages.SE:
        // Sweden: +46 XXX XX XX XX (9 digits)
        if (number.length <= 3) return `${prefix}${number}`;
        if (number.length <= 5)
          return `${prefix} ${number.slice(0, 3)} ${number.slice(3)}`;
        if (number.length <= 7)
          return `${prefix} ${number.slice(0, 3)} ${number.slice(
            3,
            5
          )} ${number.slice(5)}`;
        return `${prefix} ${number.slice(0, 3)} ${number.slice(
          3,
          5
        )} ${number.slice(5, 7)} ${number.slice(7, 9)}`;

      case Languages.NO:
        // Norway: +47 XXX XX XXX (8 digits)
        if (number.length <= 3) return `${prefix}${number}`;
        if (number.length <= 5)
          return `${prefix} ${number.slice(0, 3)} ${number.slice(3)}`;
        return `${prefix} ${number.slice(0, 3)} ${number.slice(
          3,
          5
        )} ${number.slice(5, 8)}`;

      case Languages.DE:
        // Germany: +49 XXXX XXXXXX or +49 XXX XXXXXXX (10 or 11 digits)
        if (number.length <= 4) return `${prefix}${number}`;
        return `${prefix} ${number.slice(0, 4)} ${number.slice(4, 11)}`;

      default:
        return cleanPhone;
    }
  };

  const formattedPhoneValue = useMemo(
    () =>
      formatPhoneForDisplay(
        formik.values.phone as string,
        routerLocale as keyof typeof Languages,
        phoneNumberPrefix
      ),
    [formik.values.phone, routerLocale, phoneNumberPrefix]
  );

  return (
    <div className={SignUpContainer}>
      <Input
        name="email"
        type="text"
        label={t('email', { ns: 'common' })}
        placeholder={t('yourEmail', { ns: 'common' })}
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.email && formik.errors.email
            ? { message: formik.errors.email }
            : undefined
        }
        errorMessage={formik.errors.email}
      />

      {!formik.errors.email && (
        <div className={LoginButton}>
          {t('alreadyMember', { ns: 'campaign' })}
          <Button
            appearance="text"
            size="extra-small"
            type="button"
            onClick={toggleForm}
            className={LoginButtonText}
          >
            {t('login', { ns: 'common' })}
          </Button>{' '}
          {t('onYourProfile', { ns: 'campaign' })}
        </div>
      )}

      <Input
        name="password"
        type="password"
        label={t('password', { ns: 'common' })}
        placeholder={t('yourPassword', { ns: 'account' })}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.password && formik.errors.password
            ? { message: formik.errors.password }
            : undefined
        }
        errorMessage={formik.errors.password}
      />

      <Input
        name="phone"
        type="text"
        label={t('mobile', { ns: 'common' })}
        placeholder={t('yourMobileNumber', { ns: 'account' })}
        value={formattedPhoneValue}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9+]/g, '');

          if (
            !value.startsWith(phoneNumberPrefix) ||
            value.length < phoneNumberPrefix.length
          ) {
            formik.setFieldValue('phone', phoneNumberPrefix);
            return;
          }

          formik.setFieldValue('phone', value);
        }}
        onBlur={(e) => {
          const value = e.target.value.replace(/[^0-9+]/g, '');
          formik.setFieldValue('phone', value || phoneNumberPrefix);
          formik.handleBlur(e);
        }}
        error={
          formik.touched.phone && formik.errors.phone
            ? { message: formik.errors.phone }
            : undefined
        }
        errorMessage={formik.errors.phone}
      />

      {allowMultipleCars ? (
        <MultiLicensePlateInput
          name="licensePlate"
          inputLabel={t('plate', { ns: 'common' })}
          formik={formik}
          isCampaign
          additionalTerms={additionalTerms}
          allowMultipleCars={allowMultipleCars}
          helperText={t('lpHelperText', { ns: 'campaign' })}
        />
      ) : (
        <LicensePlateInput
          name="licensePlate"
          inputLabel={t('plate', { ns: 'common' })}
          formik={formik}
          isCampaign
          additionalTerms={
            priceInformation
              ? priceInformation.content.additionalTerms
              : additionalTerms
          }
          keepAsArray={isCarRegistration && Boolean(!allowMultipleCars)}
        />
      )}

      {isCarRegistration ? (
        consentWPTranslation ? (
          <HTMLText className={ConsentText}>{consentWPTranslation}</HTMLText>
        ) : (
          ''
        )
      ) : (
        <ConsentCheckbox formik={formik} />
      )}

      {!isCarRegistration && priceInformation && (
        <CampaignSummary
          showFirstHalf={true}
          showSecondHalf={false}
          priceInformation={priceInformation}
        />
      )}

      <Button
        appearance="primary"
        size="large"
        fullWidth
        type="submit"
        className={SubmitButton}
        hasArrow={false}
        isLoading={isLoading || formik.isSubmitting || formik.isValidating}
      >
        {isLoading
          ? `${t('creatingUser', { ns: 'common' })}...`
          : submitButtonLabel}
      </Button>

      {!isCarRegistration && priceInformation && (
        <CampaignSummary
          showFirstHalf={false}
          showSecondHalf={true}
          priceInformation={priceInformation}
        />
      )}
    </div>
  );
};

export default CampaignSignUp;
